import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { isVisible } from '../../corelogic/model/section';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { getCardStyles, textMeasure } from '../../shared/styles';
import { SEO } from './SEO';
import { BlockRenderer } from './blocks/BlockRenderer';
import { ContentIcon } from './ContentIcon';
import { GoalsCircle } from './GoalsCircle';
import { PageSections } from './PageSections';
import { Section, PageHeader, SectionHeading, CardGrid } from './PageSection';

const contentUseCase = new ContentUseCase();

export const OrganisationPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getOrganisationContent(language);
  const addedBefore = (slot: string) =>
    contentUseCase.getExtraBlocksBefore('organisation', slot, language);

  const sections: Record<string, React.ReactNode> = {
    /* The three goals as the ring from the corporate identity */
    goals: isVisible(content.goals) && (
      <Section labelledBy="goals">
        <SectionHeading
          id="goals"
          title={content.goals.title}
          description={content.goals.description}
        />
        <GoalsCircle goals={content.goals.items} title={content.goals.title} />
      </Section>
    ),

    /* Vision */
    vision: isVisible(content.vision) && (
      <Section id="vision" labelledBy="vision-heading">
        <Typography
          id="vision-heading"
          variant="h2"
          component="h2"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          {content.vision.title}
        </Typography>
        {/* Centred under the heading, like the start page's opening block: the
            rule that used to stand at the lead's left edge marked it a second
            time and pushed it off the centre line the rest of the page sits
            on. Size is what sets the lead apart from the paragraphs now. */}
        <Typography
          variant="body1"
          sx={{
            mb: 4,
            fontSize: '1.1875rem',
            ...textMeasure,
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          {content.vision.lead}
        </Typography>
        {content.vision.paragraphs.map((paragraph, i) => (
          <Typography
            key={i}
            variant="body1"
            sx={{ mb: 2.5, color: 'text.secondary', ...textMeasure, mx: 'auto', textAlign: 'center' }}
          >
            {paragraph}
          </Typography>
        ))}
      </Section>
    ),

    /*
     * What the collective actually works on.
     *
     * Cards on the page's own background rather than symbols on a pale blue
     * band: the band was the loudest thing on the page and the symbols sat in
     * it as decoration. As cards each one carries a line saying what the field
     * means, which is what the vision text above used to spend five paragraphs
     * on - the eight fields *are* the concrete answer, so they say it.
     *
     * Set as the library's tiles are: the blue edge, the symbol centred over
     * the name. The one difference is that nothing here folds - a field is two
     * lines long, so hiding it behind a control would cost a click and save a
     * reader nothing. Symbol, name and line stand together.
     *
     * The heading is optional here and is currently empty: the fields speak
     * for themselves and an editor can put one back at /admin.
     */
    visionFields: isVisible(content.visionFields) && (
      <Section labelledBy={content.visionFields.title ? 'vision-fields' : undefined}>
        {content.visionFields.title && (
          <SectionHeading id="vision-fields" title={content.visionFields.title} />
        )}
        <CardGrid min={300}>
          {content.visionFields.items.map((field) => (
            <Paper
              key={field.icon}
              elevation={0}
              sx={{
                ...getCardStyles(theme, 'tile'),
                // Tighter than a card's padding: eight of these stand under one
                // another on a phone, and a field is a name and a line - room
                // set for a paragraph turns the set into a screen of scrolling.
                p: { xs: 2, md: 2.25 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <ContentIcon name={field.icon} size={26} sx={{ mb: 1 }} />
              <Typography variant="h4" component="h3" sx={{ mb: 0.5, fontSize: '1rem' }}>
                {field.label}
              </Typography>
              {field.text && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {field.text}
                </Typography>
              )}
            </Paper>
          ))}
        </CardGrid>
      </Section>
    ),
  };

  return (
    <Box>
      <SEO page="organisation" />

      <BlockRenderer blocks={addedBefore('header')} />

      {/*
       * The title band is optional here and is currently empty: the header
       * above already carries the collective's name, so an h1 repeating it word
       * for word was the same name twice over the fold. Left out, the page opens
       * on the goals ring instead. An editor can put a title back at /admin -
       * the field is there, just blank - and the band returns with it.
       *
       * Keyed on the title rather than on the subtitle: the band exists to carry
       * the h1, and an empty one is worse than none.
       */}
      {content.title && <PageHeader title={content.title} subtitle={content.subtitle} />}

      <PageSections
        page="organisation"
        sections={sections}
        order={content.sectionOrder}
        addedBefore={addedBefore}
      />
    </Box>
  );
};
